import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Banner, ErrorState, Loading, PageHeader, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { qk } from '../queries/keys';
import {
  NOTIFICATION_CHANNELS,
  fetchNotificationPrefs,
  updateNotificationPrefs,
  type NotificationChannel,
  type NotificationEvent,
  type NotificationPatch,
  type NotificationPrefs,
} from '../queries/notifications';
import { SettingsTabs } from './SettingsPage';

/**
 * What to hear about, and how.
 *
 * Four things can happen to a recording that its owner might want to know:
 * the first time anybody watches it, each further person who watches it for
 * the first time, a reaction and a comment. Each has an email switch and a
 * push switch. Push goes to the Slate app on a phone, so the page says how
 * many of those are signed in, which is the difference between "off" and
 * "nowhere to send it".
 */

const EVENTS: Array<{ key: NotificationEvent; title: string; detail: string }> = [
  { key: 'first_view', title: 'First view', detail: 'The first time anybody watches a recording of yours' },
  { key: 'new_viewer', title: 'New viewer', detail: 'Each further person who watches it for the first time' },
  { key: 'reaction', title: 'Reactions', detail: 'Somebody reacts to a moment' },
  { key: 'comment', title: 'Comments', detail: 'Somebody comments on your recording, or on one you commented on' },
];

const CHANNEL_LABEL: Record<NotificationChannel, string> = { email: 'Email', push: 'Push' };

function Switch({ on, disabled, onChange, label }: { on: boolean; disabled?: boolean; onChange: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cx(
        'relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-120',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/60',
        'disabled:pointer-events-none disabled:opacity-40',
        on ? 'border-go bg-go' : 'border-slate-600 bg-slate-800',
      )}
    >
      <span
        className={cx(
          'absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full shadow-sm transition-transform duration-120',
          on ? 'translate-x-4 bg-white' : 'translate-x-0 bg-chalk-dim',
        )}
      />
    </button>
  );
}

export function NotificationsPage() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: qk.notificationPrefs(), queryFn: fetchNotificationPrefs });

  useEffect(() => {
    track('Viewed Settings', { tab: 'notifications' });
  }, []);

  const save = useMutation({
    mutationFn: (patch: NotificationPatch) => updateNotificationPrefs(patch),
    // Flip the switch at once; the answer replaces the guess.
    onMutate: async (patch) => {
      await client.cancelQueries({ queryKey: qk.notificationPrefs() });
      const before = client.getQueryData<NotificationPrefs>(qk.notificationPrefs());
      if (before) {
        const settings = structuredClone(before.settings);
        for (const [event, channels] of Object.entries(patch)) {
          Object.assign(settings[event as NotificationEvent], channels);
        }
        client.setQueryData<NotificationPrefs>(qk.notificationPrefs(), { ...before, settings });
      }
      return { before };
    },
    onSuccess: (next) => client.setQueryData(qk.notificationPrefs(), next),
    onError: (_error, _patch, context) => {
      if (context?.before) client.setQueryData(qk.notificationPrefs(), context.before);
    },
  });

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="notifications" />
        <Loading label="Loading your notifications" />
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="notifications" />
        <ErrorState title="That did not load" message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      </>
    );
  }

  const { settings, pushDevices } = query.data;

  const flip = (event: NotificationEvent, channel: NotificationChannel) => {
    const value = !settings[event][channel];
    track('Notification Setting Changed', { event, channel, value });
    save.mutate({ [event]: { [channel]: value } });
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
      <SettingsTabs active="notifications" />

      <section className="card p-4 sm:p-5">
        <h2 className="text-h4 text-chalk">What to hear about</h2>
        <p className="mt-1 text-caption text-chalk-dim">
          When something happens to one of your recordings. Email goes to your account address. Push goes to the
          Slate app on your phone
          {pushDevices === 0
            ? ', once you have signed in there.'
            : pushDevices === 1
              ? ', which is signed in on one phone.'
              : `, which is signed in on ${pushDevices} phones.`}
          {' '}
          Everything also lands in your inbox, under the bell, whatever is switched off here. Nothing you do to your
          own recordings is ever sent to you.
        </p>

        <div className="mt-4">
          <div className="flex items-center gap-4 border-b border-hairline pb-2">
            <div className="min-w-0 flex-1" />
            {NOTIFICATION_CHANNELS.map((channel) => (
              <span key={channel} className="w-9 text-center text-caption text-chalk-faint">
                {CHANNEL_LABEL[channel]}
              </span>
            ))}
          </div>
          {EVENTS.map((event) => (
            <div key={event.key} className="flex items-center gap-4 border-b border-hairline py-3 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="text-body text-chalk">{event.title}</p>
                <p className="mt-0.5 text-caption text-chalk-faint">{event.detail}</p>
              </div>
              {NOTIFICATION_CHANNELS.map((channel) => (
                <Switch
                  key={channel}
                  on={settings[event.key][channel]}
                  label={`${event.title} by ${CHANNEL_LABEL[channel].toLowerCase()}`}
                  onChange={() => flip(event.key, channel)}
                />
              ))}
            </div>
          ))}
        </div>

        {save.isError ? (
          <div className="mt-4">
            <Banner tone="danger">{errorMessage(save.error)}</Banner>
          </div>
        ) : null}
      </section>
    </>
  );
}
